<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import coachService from '../services/coach.service'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const coaches = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pagination
const currentPage = ref(1)
const perPage = ref(25)
const totalCoaches = ref(0)
const lastPage = ref(1)
const fromItem = ref(0)
const toItem = ref(0)

// Delete modal
const showDeleteModal = ref(false)
const coachToDelete = ref<any>(null)
const deleting = ref(false)

// Dropdowns
const openDropdownId = ref<string | null>(null)

// Accordions (mobile)
const openAccordionIds = ref<Set<string>>(new Set())

// Get club UUID from init data
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Get current person role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check if user can see registration status
const canSeeRegistrationStatus = computed(() => {
  return currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR'
})

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canStore = computed(() => hasPermission('COACH_STORE'))
const canShow = computed(() => hasPermission('COACH_SHOW'))
const canUpdate = computed(() => hasPermission('COACH_UPDATE'))
const canDelete = computed(() => hasPermission('COACH_DELETE'))
const canSeeActions = computed(() => canShow.value || canUpdate.value || canDelete.value)

// Fetch coaches
const fetchCoaches = async (page: number = 1) => {
  if (!clubUuid.value) {
    error.value = t('coaches.errorNoClub')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await coachService.getCoaches(clubUuid.value, perPage.value, page)
    coaches.value = response.data || []
    currentPage.value = response.current_page || 1
    totalCoaches.value = response.total || 0
    lastPage.value = response.last_page || 1
    fromItem.value = response.from || 0
    toItem.value = response.to || 0
  } catch (err: any) {
    console.error('Error fetching coaches:', err)
    error.value = err.response?.data?.message || t('coaches.errorFetchingCoaches')
  } finally {
    loading.value = false
  }
}

// Pagination handlers
const changePage = (page: number) => {
  currentPage.value = page
  fetchCoaches(page)
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

const changePerPage = () => {
  currentPage.value = 1
  fetchCoaches(1)
}

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Delete handlers
const openDeleteModal = (coach: any) => {
  coachToDelete.value = coach
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  coachToDelete.value = null
}

const handleDelete = async () => {
  if (!coachToDelete.value || !clubUuid.value) return

  deleting.value = true

  try {
    const response = await coachService.deleteCoach(clubUuid.value, coachToDelete.value.uuid)
    const message = response?.message || t('coaches.coachDeleted')
    toastStore.error(message)

    closeDeleteModal()
    fetchCoaches(currentPage.value)
  } catch (err: any) {
    console.error('Error deleting coach:', err)
    const errorMessage = err.response?.data?.message || t('coaches.errorDeletingCoach')
    toastStore.error(errorMessage)
  } finally {
    deleting.value = false
  }
}

// Get coach display name
const getCoachName = (coach: any) => {
  const firstName = coach.first_name || ''
  const lastName = coach.last_name || ''
  return `${lastName} ${firstName}`.trim() || 'Bez nazwy'
}

// Dropdown toggle
const toggleDropdown = (coachUuid: string, event: Event) => {
  event.stopPropagation()

  if (openDropdownId.value === coachUuid) {
    openDropdownId.value = null
  } else {
    openDropdownId.value = coachUuid
  }
}

const closeDropdowns = () => {
  openDropdownId.value = null
}

// Check if coach is in last three (for dropdown positioning)
const isLastThree = (index: number) => {
  return index >= coaches.value.length - 3
}

// Toggle accordion for mobile
const toggleAccordion = (coachUuid: string) => {
  if (openAccordionIds.value.has(coachUuid)) {
    openAccordionIds.value.delete(coachUuid)
  } else {
    openAccordionIds.value.add(coachUuid)
  }
}

// Copy to clipboard
const copyToClipboard = async (text: string, type: 'phone' | 'email', event: Event) => {
  event.stopPropagation()
  if (!text || text === '') {
    return
  }

  try {
    await navigator.clipboard.writeText(text)
    const button = event.currentTarget as HTMLElement
    if (button) {
      const originalClasses = button.className
      button.classList.remove('bg-gray-100')
      button.classList.add('bg-green-200')
      setTimeout(() => {
        button.className = originalClasses
      }, 500)
    }

    // Show toast
    const message = type === 'phone' ? t('coaches.phoneCopied') : t('coaches.emailCopied')
    toastStore.success(message)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

// Close dropdowns when clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', closeDropdowns)
}

onMounted(() => {
  fetchCoaches()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('coaches.title') }} <span class="text-sm font-normal text-gray-600">({{ t('coaches.found') }}: {{ totalCoaches }})</span>
        </h1>
        <router-link
          v-if="canStore"
          to="/coaches/create"
          class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
        >
          <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
          </svg>
          <span class="md:inline hidden">{{ t('coaches.addCoach') }}</span>
          <span class="md:hidden inline">{{ t('coaches.addCoachShort') }}</span>
        </router-link>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('coaches.loadingCoaches') }}</p>
      </div>

      <!-- Coaches Table - Desktop/Tablet -->
      <div v-else class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <table class="w-full text-sm">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('coaches.licenseNumberShort') }}</th>
              <th class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('coaches.nameAndSurname') }}</th>
              <th class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('coaches.categories') }}</th>
              <th class="px-2 py-2 text-left font-semibold text-gray-700 lg:table-cell hidden">{{ t('coaches.birthDateShort') }}</th>
              <th v-if="canSeeRegistrationStatus" class="px-2 py-2 text-left font-semibold text-gray-700">{{ t('coaches.registration') }}</th>
              <th class="px-2 py-2 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="(coach, index) in coaches" :key="coach.uuid" class="hover:bg-gray-50">
              <td class="px-2 py-2 text-gray-900">{{ coach.license || '-' }}</td>
              <td class="px-2 py-2">
                <router-link
                  v-if="canShow"
                  :to="`/coaches/${coach.uuid}`"
                  class="text-blue-600 hover:underline"
                >
                  {{ getCoachName(coach) }}
                </router-link>
                <span v-else class="text-gray-900">{{ getCoachName(coach) }}</span>
                <div v-if="coach.birth_date" class="text-[10px] text-gray-500 lg:hidden">
                  {{ coach.birth_date }}
                </div>
              </td>
              <td class="px-2 py-2">
                <div class="flex gap-1 flex-wrap">
                  <template v-if="!coach.categories || coach.categories.length === 0">
                    <span class="text-gray-500 text-[10px]">-</span>
                  </template>
                  <template v-else>
                    <template v-for="category in coach.categories" :key="category.uuid">
                      <router-link
                        v-if="category.access"
                        :to="`/categories/${category.uuid}`"
                        class="inline-block px-2 py-0.5 bg-gray-300 text-gray-900 text-[10px] rounded cursor-pointer hover:bg-gray-500 transition-colors"
                      >
                        {{ category.name }}
                      </router-link>
                      <span
                        v-else
                        class="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded opacity-80"
                      >
                        {{ category.name }}
                      </span>
                    </template>
                  </template>
                </div>
              </td>
              <td class="px-2 py-2 text-gray-900 lg:table-cell hidden">{{ coach.birth_date || '-' }}</td>
              <td v-if="canSeeRegistrationStatus" class="px-2 py-2">
                <span class="text-gray-900">{{ coach.reg_date || '-' }}</span>
              </td>
              <td class="px-2 py-2 sticky right-0 bg-inherit" style="position: relative;">
                <div class="flex items-center gap-1 justify-end">
                  <span v-if="coach.registration_status !== 'registered'" class="w-2 h-2 bg-red-600 rounded-full flex-shrink-0"></span>
                  <div v-if="coach.phone" class="relative">
                    <button
                      @click="copyToClipboard(coach.phone, 'phone', $event)"
                      class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                      :data-contact-tooltip="`Tel: ${coach.phone}`"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="coach.email" class="relative">
                    <button
                      @click="copyToClipboard(coach.email, 'email', $event)"
                      class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                      :data-contact-tooltip="`Email: ${coach.email}`"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                      </svg>
                    </button>
                  </div>
                  <button
                    v-if="canSeeActions"
                    @click="toggleDropdown(coach.uuid, $event)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('common.actions') }}
                  </button>
                  <div
                    v-if="canSeeActions"
                    :class="[
                      'custom-dropdown',
                      'dropdown-bottom',
                      { 'show': openDropdownId === coach.uuid }
                    ]"
                  >
                    <router-link v-if="canShow" :to="`/coaches/${coach.uuid}`">{{ t('common.view') }}</router-link>
                    <router-link v-if="canUpdate" :to="`/coaches/${coach.uuid}/edit`">{{ t('common.edit') }}</router-link>
                    <button v-if="canDelete" class="delete-action" @click="openDeleteModal(coach)">{{ t('common.delete') }}</button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Coaches Accordion - Mobile -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div v-for="coach in coaches" :key="coach.uuid" class="bg-white border-2 border-gray-300 rounded shadow-sm">
          <!-- Accordion Header -->
          <div class="flex items-center justify-between p-3 cursor-pointer" @click="toggleAccordion(coach.uuid)">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium">
                <router-link
                  v-if="canShow"
                  :to="`/coaches/${coach.uuid}`"
                  class="text-blue-600 hover:underline"
                  @click.stop
                >
                  {{ getCoachName(coach) }}
                </router-link>
                <span v-else class="text-gray-900">{{ getCoachName(coach) }}</span>
              </div>
              <div v-if="coach.categories && coach.categories.length > 0" class="flex gap-1 mt-1 flex-wrap">
                <template v-for="category in coach.categories" :key="category.uuid">
                  <router-link
                    v-if="category.access"
                    :to="`/categories/${category.uuid}`"
                    class="inline-block px-2 py-0.5 bg-gray-300 text-gray-900 text-[10px] rounded"
                    @click.stop
                  >
                    {{ category.name }}
                  </router-link>
                  <span
                    v-else
                    class="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded opacity-80"
                  >
                    {{ category.name }}
                  </span>
                </template>
              </div>
            </div>
            <div class="flex items-center gap-2 ml-2">
              <div v-if="coach.phone" class="relative">
                <button
                  @click="copyToClipboard(coach.phone, 'phone', $event)"
                  class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                  </svg>
                </button>
              </div>
              <div v-if="coach.email" class="relative">
                <button
                  @click="copyToClipboard(coach.email, 'email', $event)"
                  class="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                  </svg>
                </button>
              </div>
              <span v-if="coach.registration_status !== 'registered'" class="w-2 h-2 bg-red-600 rounded-full flex-shrink-0"></span>
              <div v-if="canSeeActions" style="position: relative;">
                <button
                  @click="toggleDropdown(coach.uuid, $event)"
                  class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  {{ t('common.actions') }}
                </button>
                <div
                  :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === coach.uuid }]"
                >
                  <router-link v-if="canShow" :to="`/coaches/${coach.uuid}`">{{ t('common.view') }}</router-link>
                  <router-link v-if="canUpdate" :to="`/coaches/${coach.uuid}/edit`">{{ t('common.edit') }}</router-link>
                  <button v-if="canDelete" class="delete-action" @click="openDeleteModal(coach)">{{ t('common.delete') }}</button>
                </div>
              </div>
              <svg
                :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': openAccordionIds.has(coach.uuid) }]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
          <!-- Accordion Content -->
          <div v-show="openAccordionIds.has(coach.uuid)" class="border-t-2 border-gray-300 p-3 space-y-2 text-sm">
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span class="text-gray-600 text-xs">{{ t('coaches.licenseNumberShort') }}:</span>
                <div class="text-gray-900">{{ coach.license || '-' }}</div>
              </div>
              <div>
                <span class="text-gray-600 text-xs">{{ t('coaches.birthDateShort') }}:</span>
                <div class="text-gray-900">{{ coach.birth_date || '-' }}</div>
              </div>
              <div v-if="canSeeRegistrationStatus" class="col-span-2">
                <span class="text-gray-600 text-xs">{{ t('coaches.registration') }}:</span>
                <div class="text-gray-900">{{ coach.reg_date || '-' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalCoaches > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('coaches.displaying') }} {{ fromItem }} - {{ toItem }} {{ t('coaches.of') }} {{ totalCoaches }} {{ t('coaches.coachesWord') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('coaches.perPage') }}</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('coaches.deleteConfirmation') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('coaches.deleteConfirmationMessage') }} "<span class="font-semibold">{{ getCoachName(coachToDelete) }}</span>"?
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="deleting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleDelete"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="deleting"
          >
            {{ deleting ? t('coaches.deleting') : t('common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
